import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { FiArrowLeft, FiCheckCircle, FiLock, FiShield } from "react-icons/fi";
import Input from "../components/ui/Input";
import { APP_NAME } from "../constants";
import { resetPassword } from "../api";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleShowConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must contain at least 8 characters"),

    confirmPassword: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("password")], "Passwords must match"),
  });

  const onSubmit = async (values, { setSubmitting }) => {
    setMessage("");

    if (!token) {
      setMessage("This password reset link is invalid.");
      setSubmitting(false);
      return;
    }

    try {
      await resetPassword({
        token,
        password: values.password,
      });

      setSuccess(true);
    } catch (err) {
      console.error("Reset password error:", err);

      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.warning;

      setMessage(
        backendMessage ||
          "Unable to reset your password. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-[100dvh] w-full overflow-hidden bg-gray-50 lg:grid lg:grid-cols-2">

      {/* =====================================================
          LEFT
      ===================================================== */}

      <section className="relative hidden h-[100dvh] overflow-hidden bg-gradient-to-br from-purple-700 via-purple-600 to-indigo-700 px-12 py-8 text-white lg:flex lg:flex-col lg:justify-between">

        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-pink-400/20 blur-3xl" />

        <div className="relative z-10">
          <div className="inline-flex items-center gap-3">

            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-lg">
              <img
                src="/assets/images/logo.png"
                alt="Alakoze"
                className="h-10 w-10 object-contain"
              />
            </div>

            <span className="text-xl font-bold tracking-tight">
              {APP_NAME}
            </span>

          </div>
        </div>

        <div className="relative z-10 max-w-lg">

          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-md">
            <FiShield size={26} />
          </div>

          <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight xl:text-5xl">
            Secure your
            <br />
            account.
          </h1>

          <p className="mt-4 max-w-md text-[15px] leading-6 text-purple-100">
            Choose a new password for your {APP_NAME} account and you'll be ready to continue connecting with your community.
          </p>

        </div>

        <p className="relative z-10 text-xs text-purple-200">
          © {new Date().getFullYear()} {APP_NAME}
        </p>
      </section>

      {/* =====================================================
          RIGHT
      ===================================================== */}

      <section className="h-[100dvh] overflow-y-auto">

        <div className="flex min-h-full items-center justify-center px-4 py-6 sm:px-6 lg:px-10">

          <div className="w-full max-w-md">

            {/* MOBILE LOGO */}

            <div className="mb-6 flex items-center justify-center gap-2 lg:hidden">

              <img
                src="/assets/images/logo.png"
                alt="Alakoze"
                className="h-12 w-12 object-contain"
              />

              <span className="text-xl font-bold text-gray-900">
                {APP_NAME}
              </span>

            </div>

            {!success ? (
              <>

                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="mb-5 flex items-center gap-2 text-sm font-semibold text-gray-500 transition hover:text-purple-600"
                >
                  <FiArrowLeft size={17} />
                  Back to sign in
                </button>

                <span className="inline-block rounded-full bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-600">
                  Password reset
                </span>

                <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900">
                  Create a new password
                </h2>

                <p className="mt-2 text-sm leading-6 text-gray-400">
                  Enter a new password for your account.
                </p>

                {message && (
                  <div className="mt-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                    {message}
                  </div>
                )}

                <Formik
                  initialValues={{
                    password: "",
                    confirmPassword: "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={onSubmit}
                >
                  {({ isSubmitting }) => (
                    <Form className="mt-7 space-y-5">

                      <Input
                        label="New password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        handleShowPassword={handleShowPassword}
                        placeholder="Enter your new password"
                      />

                      <Input
                        label="Confirm password"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        handleShowPassword={handleShowConfirmPassword}
                        placeholder="Enter your new password again"
                      />

                      <button
                        type="submit"
                        disabled={isSubmitting || !token}
                        className="group flex w-full items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-purple-700 hover:shadow-md active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isSubmitting ? "Resetting..." : "Reset password"}

                        {!isSubmitting && (
                          <FiLock size={17} />
                        )}
                      </button>

                    </Form>
                  )}
                </Formik>

                {!token && (
                  <p className="mt-4 text-center text-sm font-medium text-red-500">
                    The reset token is missing from this link.
                  </p>
                )}

              </>
            ) : (
              <div className="text-center">

                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-green-600">
                  <FiCheckCircle size={30} />
                </div>

                <h2 className="mt-5 text-3xl font-bold tracking-tight text-gray-900">
                  Password changed
                </h2>

                <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-gray-400">
                  Your password has been reset successfully. You can now sign in with your new password.
                </p>

                <button
                  type="button"
                  onClick={() => navigate("/", { replace: true })}
                  className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md"
                >
                  Continue to sign in
                </button>

              </div>
            )}

          </div>

        </div>

      </section>

    </div>
  );
};

export default ResetPassword;